import React, { useEffect, useRef } from "react";
import gsap from "gsap";

import { createTallyLibrary } from "../../lib/TallyLibrary";
import { mapRange } from "../../lib/TallyLibrary/helpers";
import type { ShapeTarget } from "../../lib/TallyLibrary/types";
import AnimationManager from "../../lib/AnimationManager";
import { useVoiceStatus } from "../../voice/voiceStatus.ts";

/** Layout units the particles are placed in; the canvas scales them to fit. */
const SIZE = 200;
const PARTICLES = 450;
/** The ring at rest: about 60% of the canvas, leaving room for the wobble. */
const RING = 62;
const SYSTEM_ID = "voice-orb";

/** How long the logo dot takes to open into the ring. */
const OPEN_SECONDS = 1.4;

/** Working on an answer: a looser, faster swirl, like the answer animation. */
const THINKING: ShapeTarget = {
  radius: RING * 0.85,
  particleOffset: -0.6,
  noiseMultiplier: 2.5,
  magnitationMultiplier: 0.025,
};

const random = (min: number, max: number) => min + Math.random() * (max - min);

/**
 * The Tally particle mark, drawn as the voice orb.
 *
 * It opens from the logo dot into the ring the answer animation uses, then
 * follows the session: it swells with the user's voice while listening,
 * swirls while Tally thinks, and beats while it speaks.
 */
const VoiceParticles: React.FC = () => {
  const state = useVoiceStatus((status) => status.state);
  const level = useVoiceStatus((status) => status.level);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const libraryRef = useRef<ReturnType<typeof createTallyLibrary> | null>(null);
  // Level tracking waits for the opening, or it would cut it short.
  const openingRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    // A sharper backing store on dense screens. The particles are laid out in
    // SIZE units, so the context is scaled up to fill it once, here: the
    // manager draws in canvas pixels and only saves and restores around that.
    const backing = SIZE * Math.min(window.devicePixelRatio, 2);
    canvas.width = canvas.height = backing;
    context.setTransform(backing / SIZE, 0, 0, backing / SIZE, 0, 0);

    const library = createTallyLibrary({
      canvas,
      config: {
        width: SIZE,
        height: SIZE,
        margin: 1,
        dotColor: "brown",
        includeMagnitation: true,
        // The library's defaults are tuned for an 800-unit canvas.
        particleMinRadius: 2,
        particleMaxRadius: 5.5,
        particleOffset: 12,
        blobStrength: 6,
        blobMultiplier: 0.0032,
      },
    });
    libraryRef.current = library;

    const manager = AnimationManager.getInstance();
    manager.registerParticleSystem({
      id: SYSTEM_ID,
      particles: Array.from({ length: PARTICLES }, (_, i) =>
        library.createParticle("px", i),
      ),
      blobOffset: { x: 0, y: 0 },
      // In canvas pixels, as the manager measures the canvas itself.
      settings: { width: backing, height: backing, scale: 1, blobSpeed: 0.5 },
      context,
      canvas,
      isActive: true,
      mapRange,
    });

    // The library starts as the logo dot; open it into the ring.
    library.morph(
      { radius: RING, particleOffset: 0, noiseMultiplier: 1 },
      OPEN_SECONDS,
      "expo.inOut",
    );
    const opened = gsap.delayedCall(OPEN_SECONDS, () => {
      openingRef.current = false;
    });

    return () => {
      opened.kill();
      library.kill();
      libraryRef.current = null;
      manager.unregisterParticleSystem(SYSTEM_ID);
    };
  }, []);

  // Thinking and speaking set their own motion; listening is driven by level.
  useEffect(() => {
    const library = libraryRef.current;
    if (!library) return;

    if (state === "thinking" || state === "transcribing") {
      library.morph(THINKING, 1.2, "expo.out");
      return;
    }

    if (state === "speaking") {
      // No output level to follow, so beat at a speech-like, uneven cadence.
      let next: gsap.core.Tween | null = null;
      const beat = () => {
        const duration = random(0.16, 0.34);
        library.morph(
          {
            radius: RING * random(1.0, 1.16),
            particleOffset: random(-0.2, 0.3),
            noiseMultiplier: random(1, 3),
            magnitationMultiplier: 0.01,
          },
          duration,
          "sine.inOut",
        );
        next = gsap.delayedCall(duration, beat);
      };
      beat();
      return () => {
        next?.kill();
      };
    }
  }, [state]);

  useEffect(() => {
    const library = libraryRef.current;
    if (!library || state !== "listening" || openingRef.current) return;
    const swell = Math.min(level * 1.2, 0.22);
    library.morph(
      {
        radius: RING * (1 + swell),
        particleOffset: 0,
        noiseMultiplier: 1 + Math.min(level * 10, 3),
        magnitationMultiplier: 0.005,
      },
      // Short enough to track speech rather than smooth it away.
      0.12,
    );
  }, [state, level]);

  return (
    // Sized by the effect above: resizing a canvas resets its scaling.
    <canvas
      ref={canvasRef}
      style={{ display: "block", width: "100%", height: "100%" }}
    />
  );
};

export default VoiceParticles;
